import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { MapPin, Calendar, Clock, DollarSign, ShieldCheck, Mail } from "lucide-react";
import styles from "./WorkOfferInternalView.module.scss";
import BotoneraModern from "../../components/Botonera/BotoneraModern.tsx";
import Breadcrumbs from "../../components/UI/Breadcrumbs/Breadcrumbs.tsx";
import useNavVisibility from "../../hooks/useNavVisibility.ts";
import { getWorkOfferById, sendWorkOfferPostulation, resetWorkOfferPostulation, openIdentityModal } from "../../store/actions";
import WorkApplyModal from "../WorkBag/WorkApplyModal.tsx";
import ApplyExternalJobOffer from "../WorkBag/ApplyExternalJobOffer.tsx";
import ApplyInternalOfferModal from "../../components/MercadoDeTrabajo/ApplyInternalOfferModal.tsx";
import { formatPostedTime, formatDate, formatSalary } from "../../components/MercadoDeTrabajo/WorkOfferDetailsModal.tsx";
import axios from "../../axios";
import { isExternalWorkOffer } from "../../utils/jobboard/isExternalOffer.ts";
import { buildProfileEditRubrosUrl } from "../../utils/jobboard/jobboardProfileNavigation.ts";
import {
  extractOfferSkillIds,
  extractUserSkillIds,
  getOfferSkillLabels,
  userHasMatchingOfferSkill,
} from "../../utils/jobboard/offerSkillMatch.ts";
import { parseUserCvFromProfile } from "../../utils/userCv.ts";
import OfferSkillsMismatchModal from "../../components/MercadoDeTrabajo/OfferSkillsMismatchModal.tsx";

const WorkOfferInternalView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const { ocultarEnMobile, mobileStylesFromParent } = useNavVisibility();

  const offerState = useSelector((state: any) => state.register?.currentWorkOffer);
  const workOfferPostulation = useSelector((state: any) => state.register?.workOfferPostulation);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showExternalContactModal, setShowExternalContactModal] = useState(false);
  const [showApplyInternalModal, setShowApplyInternalModal] = useState(false);
  const [showSkillsMismatchModal, setShowSkillsMismatchModal] = useState(false);
  const [alreadyApplied, setAlreadyApplied] = useState(false);
  const [appliedAt, setAppliedAt] = useState<string | null>(null);
  const appliedFromInternalRef = useRef(false);

  const userData = useSelector((state: any) => state.general?.userData);
  const userIdentityValidated: boolean = Boolean(
    userData?.jobBoard?.identity_validated ?? userData?.identity_validated ?? false
  );
  const userEmail = (userData?.email ?? userData?.username ?? "").toLowerCase();
  const profileCv = useMemo(() => parseUserCvFromProfile(userData), [userData]);

  const handleGoBack = useCallback(() => {
    navigate("/bolsa-trabajo");
  }, [navigate]);

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    dispatch(resetWorkOfferPostulation());
    dispatch(getWorkOfferById(id)).finally(() => setIsLoading(false));
  }, [dispatch, id]);

  useEffect(() => {
    if (workOfferPostulation && appliedFromInternalRef.current) {
      appliedFromInternalRef.current = false;
      handleGoBack();
    }
  }, [workOfferPostulation, handleGoBack]);

  const offer = useMemo(() => {
    if (!offerState) return null;
    const offerId = String(offerState.id ?? "");
    if (offerId !== String(id ?? "")) return null;
    return offerState;
  }, [offerState, id]);

  const companyLabel = useMemo(() => {
    if (!offer?.user) return "Publicador";
    if (offer.user.type === "COMPANY") return offer.user.razon_social ?? "Empresa";
    const name = offer.user.name ?? "";
    const last = offer.user.last_name ?? "";
    const full = `${name} ${last}`.trim();
    return full || "Publicador";
  }, [offer?.user]);

  const companyImage = useMemo(() => {
    const source = offer?.user?.image_profile;
    if (source && source.trim().length > 0) return source;
    return "/placeholder.svg?height=96&width=96";
  }, [offer?.user?.image_profile]);

  const isExternalOffer = useMemo(() => isExternalWorkOffer(offer), [offer]);

  const postedLabel = offer ? formatPostedTime(offer.createdAt) : "";
  const primaryZone = offer?.workZones?.[0]?.jurisdiccion?.name ?? offer?.workZones?.[0]?.city ?? "Ubicación a confirmar";
  const startLabel = offer?.immediateStart ? "Inicio inmediato" : formatDate(offer?.startDate);
  const activeUntil = formatDate(offer?.activeUntil);
  const salaryLabel = formatSalary(offer?.salary, offer?.salaryType);
  const offerSkillLabels = useMemo(
    () => getOfferSkillLabels(offer?.skill),
    [offer?.skill]
  );
  const skillNames = offerSkillLabels;
  const zoneLabels = (offer?.workZones ?? []).map((zone, index) => zone?.jurisdiccion?.name ?? zone?.city ?? `Zona ${index + 1}`);
  const hasDescription = Boolean(offer?.description && offer.description.trim().length > 0);

  useEffect(() => {
    const authToken = localStorage.getItem("access-Token-Edify");
    const offerId = Number(offer?.id);
    if (!authToken || !offerId || Number.isNaN(offerId) || isExternalOffer) {
      setAlreadyApplied(false);
      setAppliedAt(null);
      return;
    }
    axios
      .get(`/job-request/${offerId}/postulation-status`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        setAlreadyApplied(res?.data?.alreadyApplied === true);
        setAppliedAt(res?.data?.appliedAt ?? null);
      })
      .catch(() => {
        setAlreadyApplied(false);
        setAppliedAt(null);
      });
  }, [offer?.id, isExternalOffer]);

  const handleApplyInternal = () => {
    if (!offer) return;

    const userSkillIds = extractUserSkillIds(userData?.skills);
    const offerSkillIds = extractOfferSkillIds(offer?.skill);
    const matches = userHasMatchingOfferSkill(userData?.skills, offer?.skill);
    console.log("[bolsa-trabajo] skill match", {
      offerId: offer.id,
      isExternalOffer,
      userSkillIds,
      offerSkillIds,
      userSkillsRaw: userData?.skills,
      offerSkillsRaw: offer?.skill,
      matches,
    });

    if (!matches) {
      setShowSkillsMismatchModal(true);
      return;
    }

    if (isExternalOffer) {
      setShowExternalContactModal(true);
      return;
    }

    if (!userEmail) {
      setShowValidationModal(true);
      return;
    }

    if (alreadyApplied) {
      setShowApplyInternalModal(true);
      return;
    }

    setShowApplyInternalModal(true);
  };

  const handleApplyInternalSubmit = async (data: { cv: File | ""; coverLetter: string }) => {
    if (!offer) return;
    if (!userHasMatchingOfferSkill(userData?.skills, offer.skill)) return;

    const jobRequestId = Number(offer.id);
    if (Number.isNaN(jobRequestId)) return;

    setIsSubmitting(true);
    appliedFromInternalRef.current = true;

    const email = userEmail;
    const name = [userData?.name, userData?.last_name].filter(Boolean).join(" ") || userData?.razon_social || "Usuario Edify";
    const phone = userData?.phone ?? "";

    try {
      await dispatch(
        sendWorkOfferPostulation({
          jobRequestId,
          email,
          name,
          phone,
          cv: data.cv || "",
          coverLetter: data.coverLetter || "",
        })
      );
      setShowApplyInternalModal(false);
    } catch {
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenIdentityValidation = () => {
    dispatch(openIdentityModal(true, userEmail || userData?.username || ""));
  };

  const handleGoToProfileRubros = () => {
    const userId = userData?.id;
    if (!userId || !offer?.id) return;
    setShowSkillsMismatchModal(false);
    const returnTo = `/bolsa-trabajo/${offer.id}`;
    navigate(buildProfileEditRubrosUrl(userId, returnTo));
  };

  const primaryActionLabel = "Postularme";

  return (
    <div className={styles.page}>
      {ocultarEnMobile && <BotoneraModern mobileStylesFromParent={mobileStylesFromParent} />}
      <div className={styles.wrapper}>
        <div className={styles.breadcrumbs}>
          <Breadcrumbs />
        </div>
        <main className={styles.content}>
          {(!isLoading && offer) ? (
            <div className={styles.detailsWrapper}>
              <article className={styles.detailsCard}>
                  <header className={styles.detailsCardHeader}>
                    <div className={styles.headerMain}>
                      <div className={styles.headerAvatar}>
                        <img src={companyImage} alt={`Perfil de ${companyLabel}`} />
                      </div>
                      <div className={styles.headerInfo}>
                        <div className={styles.headerMeta}>
                          <span className={styles.metaPill}>{postedLabel}</span>
                          <span className={styles.headerLabel}>Publicada por</span>
                        </div>
                        <h2 className={styles.companyName}>{companyLabel}</h2>
                      </div>
                    </div>
                    {isExternalOffer && <span className={styles.externalBadge}>Búsqueda externa</span>}
                  </header>

                  <section className={styles.infoSection}>
                    <div className={styles.infoGrid}>
                      <div className={styles.infoItem}>
                        <MapPin size={16} />
                        <span>{primaryZone}</span>
                      </div>
                      <div className={styles.infoItem}>
                        <Calendar size={16} />
                        <span>{startLabel}</span>
                      </div>
                      <div className={styles.infoItem}>
                        <Clock size={16} />
                        <span>Activa hasta {activeUntil}</span>
                      </div>
                      <div className={styles.infoItem}>
                        <DollarSign size={16} />
                        <span>{salaryLabel}</span>
                      </div>
                    </div>
                  </section>

                  <section className={styles.narrativeBlock}>
                    <p className={styles.narrativeParagraph}>
                      Esta búsqueda está pensada para alguien con experiencia en {skillNames.slice(0, 3).join(", ") || "el rubro seleccionado"}. {isExternalOffer ? "El empleador" : companyLabel} necesita cubrir tareas en {primaryZone}.<br />
                      La oferta queda visible hasta {activeUntil} y el inicio estimado es {startLabel}.
                    </p>
                    <p className={styles.narrativeParagraph}>
                      El salario ofertado es {salaryLabel}. Podría incluir ajustes según la modalidad acordada con {isExternalOffer ? "quien publicó la oferta" : "el equipo de " + companyLabel}.<br />
                      Si tenés disponibilidad en {zoneLabels.join(", ") || primaryZone}, estás listo para continuar.
                    </p>
                    {isExternalOffer && (
                      <p className={styles.narrativeParagraph}>
                        Esta es una búsqueda externa: Edify hará todo lo posible para acercar tu propuesta a quien publicó la oferta, aunque no podemos garantizar respuesta o contratación.<br />
                        La contratación final siempre la gestiona directamente la empresa.
                      </p>
                    )}
                  </section>

                  {hasDescription && (
                    <section className={styles.section}>
                      <h3 className={styles.sectionTitle}>Lo que cuenta el equipo</h3>
                      <p className={styles.description}>{offer.description}</p>
                    </section>
                  )}

                  {!userIdentityValidated && (
                    <div className={styles.identityNotice}>
                      <ShieldCheck size={16} />
                      Podés validar tu identidad para destacar tu perfil en las postulaciones.
                    </div>
                  )}

                  {!isExternalOffer && userIdentityValidated && !userEmail && (
                    <div className={styles.infoNotice}>
                      <Mail size={16} />
                      Necesitamos un correo en tu perfil para enviar la postulación. Podés ingresarlo a continuación.
                    </div>
                  )}

                  <footer className={styles.actions}>
                    <button type="button" className={styles.secondaryAction} onClick={handleGoBack}>
                      Volver a ofertas
                    </button>
                    {!userIdentityValidated && (
                      <button
                        type="button"
                        className={styles.secondaryAction}
                        onClick={handleOpenIdentityValidation}
                        disabled={isSubmitting}
                      >
                        Validar identidad (opcional)
                      </button>
                    )}
                    <button
                      type="button"
                      className={styles.primaryAction}
                      onClick={handleApplyInternal}
                      disabled={!isExternalOffer && isSubmitting}
                    >
                      {primaryActionLabel}
                    </button>
                  </footer>
                </article>
            </div>
          ) : null}
          {!isLoading && !offer && <div className={styles.empty}>No encontramos la oferta solicitada.</div>}
        </main>
      </div>

      {showValidationModal && offer && (
        <WorkApplyModal
          workOffer={offer}
          setShowApplyModal={setShowValidationModal}
          forbiddenClose={false}
          setShowForm={() => {}}
          skipWorkBagUserCheck
          prefilledEmail={userEmail || undefined}
          identityValidated={userIdentityValidated}
        />
      )}
      {showExternalContactModal && offer && (
        <ApplyExternalJobOffer
          workOffer={offer}
          isOpen={showExternalContactModal}
          onClose={() => setShowExternalContactModal(false)}
        />
      )}
      {showSkillsMismatchModal && offer && (
        <OfferSkillsMismatchModal
          isOpen={showSkillsMismatchModal}
          onClose={() => setShowSkillsMismatchModal(false)}
          skillLabels={offerSkillLabels}
          onGoToProfileRubros={handleGoToProfileRubros}
        />
      )}
      {showApplyInternalModal && offer && (
        <ApplyInternalOfferModal
          isOpen={showApplyInternalModal}
          onClose={() => setShowApplyInternalModal(false)}
          onSubmit={handleApplyInternalSubmit}
          isSubmitting={isSubmitting}
          alreadyApplied={alreadyApplied}
          appliedAt={appliedAt}
          profileCvUrl={profileCv.url}
          profileCvUpdatedAt={profileCv.updatedAt}
          isAuthenticated={Boolean(userData?.id)}
        />
      )}
    </div>
  );
};

export default WorkOfferInternalView;
